/* eslint-disable react/require-default-props */
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Editor } from '@tiptap/core';
import { Node as PMNode } from '@tiptap/pm/model';

import type { ProposalDeclarationFieldSituacao } from 'services/actions/ai_management_proposal_documents';
import type { DeclarationField } from 'services/ai_management_proposal_documents';
import { CampoAttrs, TextAlign, extensoesDaDeclaracao } from './extensions';
import { Container } from './styles';

export interface DeclarationEditorField {
  id: string;
  valor: string | null;
  situacao: ProposalDeclarationFieldSituacao;
}

export interface DeclarationEditorHandle {
  acceptAll: () => void;
  undoFill: () => void;
}

interface AiManagementDeclarationEditorProps {
  // Lido só ao montar: as respostas do PATCH não recriam o editor (o cursor não pula).
  initialHtml: string;
  fields: DeclarationField[];
  readOnly?: boolean;
  ariaLabel?: string;
  onChange: (html: string, campos: DeclarationEditorField[]) => void;
  // "Aceitar preenchimento" e "Desfazer preenchimento" na barra, à direita (como no protótipo).
  showFillActions?: boolean;
  canAcceptFill?: boolean;
}

const camposDoDocumento = (doc: PMNode): DeclarationEditorField[] => {
  const campos: DeclarationEditorField[] = [];
  doc.descendants((node) => {
    if (node.type.name !== 'campo' || !node.attrs.id) return;
    const attrs = node.attrs as CampoAttrs;
    if (campos.some((campo) => campo.id === attrs.id)) return;
    campos.push({
      id: attrs.id as string,
      valor: attrs.valor.trim() || null,
      situacao: attrs.situacao,
    });
  });
  return campos;
};

// Troca os atributos dos campos sem entrar no histórico (desfazer volta só o que o cliente fez).
const mudarCampos = (
  editor: Editor,
  mudar: (attrs: CampoAttrs) => Partial<CampoAttrs> | null,
  historico = true,
) => {
  const { tr } = editor.state;
  let mudou = false;
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name !== 'campo') return;
    const novos = mudar(node.attrs as CampoAttrs);
    if (!novos) return;
    tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...novos });
    mudou = true;
  });
  if (!mudou) return;
  if (!historico) tr.setMeta('addToHistory', false);
  editor.view.dispatch(tr);
};

const ALINHAMENTOS: { valor: TextAlign; icone: string; titulo: string }[] = [
  { valor: 'left', icone: 'format_align_left', titulo: 'Alinhar à esquerda' },
  { valor: 'center', icone: 'format_align_center', titulo: 'Centralizar' },
  { valor: 'right', icone: 'format_align_right', titulo: 'Alinhar à direita' },
  { valor: 'justify', icone: 'format_align_justify', titulo: 'Justificar' },
];

// Editor de texto rico das declarações, reaproveitado no texto do edital, no "Escrever a
// declaração" e nos modelos. TipTap direto no @tiptap/core, como o editor de petições.
const AiManagementDeclarationEditor = forwardRef<
  DeclarationEditorHandle,
  AiManagementDeclarationEditorProps
>(
  (
    {
      initialHtml,
      fields,
      readOnly = false,
      ariaLabel = 'Texto da declaração',
      onChange,
      showFillActions = false,
      canAcceptFill = true,
    },
    ref,
  ) => {
    const montagemRef = useRef<HTMLDivElement>(null);
    const [editor, setEditor] = useState<Editor | null>(null);
    const [, redesenhar] = useState(0);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const fieldsRef = useRef(fields);
    fieldsRef.current = fields;

    useEffect(() => {
      if (!montagemRef.current) return undefined;
      const novo = new Editor({
        element: montagemRef.current,
        extensions: extensoesDaDeclaracao(),
        content: initialHtml || '',
        editable: !readOnly,
        editorProps: {
          attributes: {
            role: 'textbox',
            'aria-multiline': 'true',
            'aria-label': ariaLabel,
            spellcheck: 'true',
            class: 'declaration-editor decl-page',
          },
        },
      });
      // O HTML só traz o id e o texto do campo; rótulo, sugerido e situação vêm do array `campos`.
      const porId = new Map(fieldsRef.current.map((field) => [field.id, field]));
      mudarCampos(
        novo,
        (attrs) => {
          const field = attrs.id ? porId.get(attrs.id) : undefined;
          if (!field) return null;
          return {
            valor: field.value ?? '',
            rotulo: field.label,
            sugerido: field.suggested,
            situacao: field.situacao,
          };
        },
        false,
      );
      novo.on('transaction', () => redesenhar((n) => n + 1));
      novo.on('update', () =>
        onChangeRef.current(novo.getHTML(), camposDoDocumento(novo.state.doc)),
      );
      setEditor(novo);
      return () => novo.destroy();
      // Montado uma vez: initialHtml e readOnly trocam pela key do componente.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        acceptAll: () => {
          if (editor)
            mudarCampos(editor, (attrs) =>
              attrs.situacao === 'sugerido' ? { situacao: 'aceito' } : null,
            );
        },
        undoFill: () => {
          if (!editor) return;
          mudarCampos(editor, (attrs) => ({
            valor: attrs.sugerido ?? '',
            situacao: attrs.sugerido ? 'sugerido' : 'pendente',
          }));
        },
      }),
      [editor],
    );

    const botao = (
      icone: string,
      titulo: string,
      ativo: boolean,
      acao: () => void,
      gap = false,
      desabilitado = false,
    ) => (
      <button
        key={icone}
        type="button"
        className={`${ativo ? 'active' : ''}${gap ? ' tool-gap' : ''}`}
        disabled={desabilitado}
        title={titulo}
        aria-label={titulo}
        aria-pressed={ativo}
        onMouseDown={(event) => event.preventDefault()}
        onClick={acao}
      >
        <i className="material-icons-outlined" aria-hidden="true">
          {icone}
        </i>
      </button>
    );

    const alinhado = (valor: TextAlign) =>
      !!editor &&
      (editor.isActive({ textAlign: valor }) ||
        (valor === 'left' && !ALINHAMENTOS.some((a) => editor.isActive({ textAlign: a.valor }))));

    return (
      <Container>
        {editor && !readOnly && (
          <div className="decl-toolbar" role="toolbar" aria-label="Formatação da declaração">
            <div className="decl-tools">
              {botao(
                'undo',
                'Desfazer',
                false,
                () => editor.chain().focus().undo().run(),
                false,
                !editor.can().undo(),
              )}
              {botao(
                'redo',
                'Refazer',
                false,
                () => editor.chain().focus().redo().run(),
                false,
                !editor.can().redo(),
              )}
              {botao(
                'format_bold',
                'Negrito',
                editor.isActive('bold'),
                () => editor.chain().focus().toggleBold().run(),
                true,
              )}
              {botao('format_italic', 'Itálico', editor.isActive('italic'), () =>
                editor.chain().focus().toggleItalic().run(),
              )}
              {botao('format_underlined', 'Sublinhado', editor.isActive('underline'), () =>
                editor.chain().focus().toggleUnderline().run(),
              )}
              {ALINHAMENTOS.map((a, index) =>
                botao(
                  a.icone,
                  a.titulo,
                  alinhado(a.valor),
                  () => editor.chain().focus().setTextAlign(a.valor).run(),
                  index === 0,
                ),
              )}
              {botao(
                'format_list_bulleted',
                'Lista com marcadores',
                editor.isActive('bulletList'),
                () => editor.chain().focus().toggleBulletList().run(),
                true,
              )}
              {botao('format_list_numbered', 'Lista numerada', editor.isActive('orderedList'), () =>
                editor.chain().focus().toggleOrderedList().run(),
              )}
              {botao(
                'format_clear',
                'Limpar formatação',
                false,
                () => editor.chain().focus().unsetAllMarks().setTextAlign('left').run(),
                true,
              )}
            </div>
            {showFillActions && (
              <div className="decl-fill-actions">
                <button
                  type="button"
                  className="fill-accept"
                  disabled={!canAcceptFill}
                  onClick={() =>
                    mudarCampos(editor, (attrs) =>
                      attrs.situacao === 'sugerido' ? { situacao: 'aceito' } : null,
                    )
                  }
                >
                  <i className="material-icons-outlined" aria-hidden="true">
                    check
                  </i>
                  Aceitar preenchimento
                </button>
                <button
                  type="button"
                  className="fill-reject"
                  onClick={() =>
                    mudarCampos(editor, (attrs) => ({
                      valor: attrs.sugerido ?? '',
                      situacao: attrs.sugerido ? 'sugerido' : 'pendente',
                    }))
                  }
                >
                  <i className="material-icons-outlined" aria-hidden="true">
                    close
                  </i>
                  Desfazer preenchimento
                </button>
              </div>
            )}
          </div>
        )}
        <div className={`decl-page-wrap${readOnly ? ' read-only' : ''}`}>
          <div ref={montagemRef} />
        </div>
      </Container>
    );
  },
);

AiManagementDeclarationEditor.displayName = 'AiManagementDeclarationEditor';

export default AiManagementDeclarationEditor;
